import { useState } from 'react';
import * as Yup from 'yup';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
// routes
import { PATH_AUTH } from '../../routes/paths';
// auth
import { useAuthContext } from '../../auth/useAuthContext';
// components
import Iconify from '../../components/iconify';
import FormProvider, { RHFTextField } from '../../components/hook-form';

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  afterSubmit?: string;
};

export default function AuthLoginForm() {
  const { login, logout } = useAuthContext();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: 'admin@ailyn.io',
    password: 'password',
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const user = await login(data.email, data.password);
      
      // Restriction: Only Sub-Org Managers can access this interface
      const isManager = user?.role === 'MANAGER';
      
      if (!isManager) {
        await logout();
        throw new Error('NOT_AUTHORIZED_ROLE');
      }
    } catch (error) {
      console.error(error);
      
      let message = "Email ou mot de passe incorrect.";
      
      if (error.message === 'NOT_AUTHORIZED_ROLE') {
         message = "Seuls les administrateurs de la sous-organisation peuvent se connecter à cette interface.";
      }
      
      setError('afterSubmit', {
        ...error,
        message,
      });
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField 
          name="email" 
          label="Email address" 
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="mdi:email-outline" width={22} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }
          }}
        />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="mdi:lock-outline" width={22} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }
          }}
        />
      </Stack>

      <Stack alignItems="flex-end" sx={{ my: 2 }}>
        <Link
          component={RouterLink}
          to={PATH_AUTH.resetPassword}
          variant="body2"
          color="text.secondary"
          underline="always"
          sx={{
            textDecoration: 'underline',
          }}
        >
          Mot de passe oublié ?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting}
        sx={{
          bgcolor: '#2563EB',
          color: 'common.white',
          '&:hover': {
            bgcolor: '#1D4ED8',
          },
          boxShadow: 'none',
        }}
      >
        Connexion
      </LoadingButton>
    </FormProvider>
  );
}
